'use client';

import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent,
} from 'react';

import { faVideoSlash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslations } from 'use-intl';

import { Button } from '@/ui';

import ProjectUsabilityCameraVideo from './project-usability-camera-video';
import {
  CAMERA_BUBBLE_MARGIN_PX,
  CAMERA_BUBBLE_SIZE_PX,
} from './project-usability-camera.consts';
import { useUsabilityCamera } from './project-usability-camera.hooks';

interface Props {
  projectId: string;
  previewActive?: boolean;
}

interface DragOrigin {
  dx: number;
  dy: number;
  startX: number;
  startY: number;
  maxX: number;
  maxY: number;
}

interface BubbleOffsetStyle extends CSSProperties {
  '--ut-cam-x': string;
  '--ut-cam-y': string;
}

const DRAG_THRESHOLD_PX = 4;

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function boundsSize(el: HTMLDivElement | null): {
  width: number;
  height: number;
} {
  if (!el) return { width: window.innerWidth, height: window.innerHeight };
  const rect = el.getBoundingClientRect();
  return { width: rect.width, height: rect.height };
}

function maxOffset(size: number): number {
  return Math.max(
    0,
    size - CAMERA_BUBBLE_SIZE_PX - 2 * CAMERA_BUBBLE_MARGIN_PX
  );
}

export default function ProjectUsabilityCameraBubble({
  projectId,
  previewActive = false,
}: Props) {
  const t = useTranslations('Usability');

  const boundsRef = useRef<HTMLDivElement>(null);
  const dragOriginRef = useRef<DragOrigin | null>(null);
  const movedRef = useRef(false);

  const [offset, setOffset] = useState({ dx: 0, dy: 0 });

  const {
    isCameraActive,
    isCameraOn,
    isRecording,
    cameraStream,
    enableCamera,
    pauseCamera,
  } = useUsabilityCamera(projectId);

  useEffect(() => {
    const onResize = () => {
      const { width, height } = boundsSize(boundsRef.current);
      setOffset((prev) => ({
        dx: clamp(prev.dx, 0, maxOffset(width)),
        dy: clamp(prev.dy, -maxOffset(height), 0),
      }));
    };

    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  if (!isCameraActive || (!isRecording && !previewActive)) {
    return null;
  }

  const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    movedRef.current = false;
    const { width, height } = boundsSize(boundsRef.current);
    dragOriginRef.current = {
      dx: offset.dx,
      dy: offset.dy,
      startX: event.clientX,
      startY: event.clientY,
      maxX: maxOffset(width),
      maxY: maxOffset(height),
    };
  };

  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const origin = dragOriginRef.current;
    if (!origin) return;

    const ddx = event.clientX - origin.startX;
    const ddy = event.clientY - origin.startY;
    if (
      !movedRef.current &&
      (Math.abs(ddx) > DRAG_THRESHOLD_PX || Math.abs(ddy) > DRAG_THRESHOLD_PX)
    ) {
      movedRef.current = true;
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
        // eslint-disable-next-line no-empty -- pointer capture is best-effort
      } catch {}
    }

    setOffset({
      dx: clamp(origin.dx + ddx, 0, origin.maxX),
      dy: clamp(origin.dy + ddy, -origin.maxY, 0),
    });
  };

  const handlePointerUp = (event: PointerEvent<HTMLDivElement>) => {
    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
      // eslint-disable-next-line no-empty -- pointer capture is best-effort
    } catch {}
    dragOriginRef.current = null;
  };

  const handleReenable = () => {
    if (movedRef.current) return;
    void enableCamera();
  };

  const offsetStyle: BubbleOffsetStyle = {
    '--ut-cam-x': `${offset.dx}px`,
    '--ut-cam-y': `${offset.dy}px`,
  };

  return (
    <>
      <div
        ref={boundsRef}
        aria-hidden='true'
        className='pointer-events-none fixed inset-0'
      />
      <div
        className='fixed bottom-6 left-6 z-50 size-36 translate-x-(--ut-cam-x) translate-y-(--ut-cam-y) touch-none rounded-full select-none'
        // eslint-disable-next-line react/forbid-dom-props -- runtime drag offset
        style={offsetStyle}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        data-test='ut-camera-bubble'
      >
        {isCameraOn ? (
          <div className='group relative size-full overflow-hidden rounded-full bg-gray-900 shadow-xl ring-2 ring-white/70'>
            {cameraStream && (
              <ProjectUsabilityCameraVideo
                stream={cameraStream}
                className='size-full -scale-x-100 object-cover'
              />
            )}
            <Button
              type='button'
              variant='secondary'
              size='icon-sm'
              shape='pill'
              className='absolute bottom-1 left-1/2 -translate-x-1/2 opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100 pointer-coarse:opacity-100'
              onPointerDown={(event) => event.stopPropagation()}
              onClick={() => pauseCamera()}
              aria-label={t('cameraTurnOff')}
              data-test='ut-camera-bubble-off'
            >
              <FontAwesomeIcon
                icon={faVideoSlash}
                className='text-xs'
                aria-hidden='true'
              />
            </Button>
          </div>
        ) : (
          <Button
            type='button'
            variant='primary'
            shape='pill'
            className='size-full bg-gray-900 hover:bg-gray-800'
            onClick={handleReenable}
            aria-label={t('cameraTurnOn')}
            data-test='ut-camera-bubble-reenable'
          >
            <FontAwesomeIcon
              icon={faVideoSlash}
              className='text-2xl'
              aria-hidden='true'
            />
          </Button>
        )}
      </div>
    </>
  );
}
